"use client"

import type { DayButtonProps, NavProps } from "react-day-picker"
import * as React from "react"
import { isSameDay } from "date-fns"
import {
	IconArrowTurnRightOutline18 as ArrowTurnRight,
	IconChevronLeftOutline18 as ChevronLeft,
	IconChevronRightOutline18 as ChevronRight,
} from "nucleo-ui-outline-18"
import {
	DayButton,
	DayPicker,
	isDateRange,
	useDayPicker,
} from "react-day-picker"

import { cn } from "@productlane/lib/src/browser"

import { IconButton } from "../icon-button"
import { POPOVER_AUTOFOCUS_ATTRIBUTE } from "../popover"
import { Tooltip } from "../tooltip"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

const navButtonClassName = "dark:hover:bg-white/10"

function CalendarNav({
	onPreviousClick,
	onNextClick,
	previousMonth,
	nextMonth,
	...props
}: NavProps) {
	const { goToMonth, labels } = useDayPicker()
	return (
		<nav {...props}>
			<Tooltip label="Go to today">
				<IconButton
					variant="ghost"
					Icon={ArrowTurnRight}
					aria-label="Go to today"
					className={navButtonClassName}
					onClick={() => goToMonth(new Date())}
				/>
			</Tooltip>
			<IconButton
				variant="ghost"
				Icon={ChevronLeft}
				aria-label={labels.labelPrevious(previousMonth)}
				disabled={!previousMonth}
				className={navButtonClassName}
				onClick={onPreviousClick}
			/>
			<IconButton
				variant="ghost"
				Icon={ChevronRight}
				aria-label={labels.labelNext(nextMonth)}
				disabled={!nextMonth}
				className={navButtonClassName}
				onClick={onNextClick}
			/>
		</nav>
	)
}

// Keyed on the roving tab stop because a dialog's focus trap blurs the day on
// mount, and that blur clears `focused`.
function CalendarDayButton(props: DayButtonProps) {
	const { dayPickerProps } = useDayPicker()
	const isAutoFocusTarget = !!dayPickerProps.autoFocus && props.tabIndex === 0
	return (
		<DayButton
			{...props}
			{...{ [POPOVER_AUTOFOCUS_ATTRIBUTE]: isAutoFocusTarget || undefined }}
		/>
	)
}

// Weekend columns assume weekStartsOn={1}: Saturday and Sunday are cells 6 and 7.
const weekendHeader =
	"[&>th:nth-child(n+6)]:bg-black/[0.03] [&>th:nth-child(n+6)]:text-tertiary dark:[&>th:nth-child(n+6)]:bg-white/[0.03] [&>th:nth-child(6)]:rounded-tl-lg [&>th:nth-child(7)]:rounded-tr-lg"
const weekendColumn =
	"[&>td:nth-child(n+6)]:bg-black/[0.03] [&>td:nth-child(n+6)]:text-tertiary dark:[&>td:nth-child(n+6)]:bg-white/[0.03] [&:last-child>td:nth-child(6)]:rounded-bl-lg [&:last-child>td:nth-child(7)]:rounded-br-lg"

const rangeBand =
	"before:absolute before:inset-y-0 before:bg-black/[0.05] dark:before:bg-white/[0.06]"

function CalendarInner({
	className,
	classNames,
	showOutsideDays = true,
	fixedWeeks = true,
	...props
}: CalendarProps) {
	const singleDayRange =
		props.mode === "range" &&
		isDateRange(props.selected) &&
		!!props.selected.from &&
		!!props.selected.to &&
		isSameDay(props.selected.from, props.selected.to)

	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			fixedWeeks={fixedWeeks}
			navLayout="after"
			className={cn("p-2", className)}
			weekStartsOn={1}
			classNames={{
				months: "flex flex-col gap-4 sm:flex-row",
				month: "relative flex flex-col gap-2",
				month_caption: "flex h-9 items-center px-2",
				caption_label: "text-sm font-medium text-primary",
				nav: "absolute right-0 top-1 z-10 flex items-center gap-0.5",
				month_grid: "border-collapse",
				weekdays: cn(
					"flex border-b border-gray-200 dark:border-gray-700/80",
					weekendHeader,
				),
				weekday:
					"flex h-9 w-10 items-center justify-center text-sm font-normal text-secondary",
				week: cn("flex", weekendColumn),
				day: "relative flex h-[34px] w-10 items-center justify-center p-0 text-sm text-primary",
				day_button:
					"relative flex h-7 w-7 items-center justify-center rounded-full outline-none hover:bg-black/[0.06] focus-visible:ring-2 focus-visible:ring-brand-600 disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-white/10",
				selected:
					"[&>button]:bg-gray-900 [&>button]:font-medium [&>button]:text-white [&>button:hover]:bg-gray-700 dark:[&>button]:bg-gray-100 dark:[&>button]:text-gray-900 dark:[&>button:hover]:bg-gray-300",
				today:
					"[&>button]:font-medium [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-black/15 dark:[&>button]:ring-white/20",
				outside: "[&>button]:text-tertiary [&>button]:opacity-60",
				disabled: "",
				range_start: singleDayRange
					? ""
					: cn(rangeBand, "before:left-1/2 before:right-0"),
				range_end: singleDayRange
					? ""
					: cn(rangeBand, "before:left-0 before:right-1/2"),
				range_middle: cn(
					rangeBand,
					"before:inset-x-0 [&>button]:!bg-transparent [&>button]:!font-normal [&>button]:!text-inherit [&>button:hover]:!bg-black/[0.06] dark:[&>button:hover]:!bg-white/10",
				),
				hidden: "invisible",
				...classNames,
			}}
			components={{ Nav: CalendarNav, DayButton: CalendarDayButton }}
			{...props}
		/>
	)
}
CalendarInner.displayName = "Calendar"

export const Calendar = React.memo(CalendarInner)
